'use client';

import {
  type InventoryResource,
  type Permission,
  PermissionCategory,
  type Role,
} from '@repo/api-gw-sdk';
import { createContext, type PropsWithChildren } from 'react';

import { rolesComparer } from '@/configs/roles';

import { Permissions, type PermissionDescriptor } from './permissions';
import { useNonProjectDal } from './useNonProjectDal';
import { useUser } from './useUser';

const isAuthorized = (scope: string, roleScopes: string[]) => {
  const [permission, action] = scope.split(':');
  const optionalScopes = [
    scope,
    `${permission}:all`,
    `admin:${action}`,
    'admin:all',
  ];
  return optionalScopes.some((x) => roleScopes.includes(x));
};

export const RolesContext = createContext<{
  roles: Role[];
  rolesMap: Record<string, Role>;
  permissions: Record<string, PermissionDescriptor>;
  isGlobalRole: (role?: Role) => boolean;
  isAuthorized: (scope: string, roleOverride?: Role) => boolean;
  isAuthorizedResource: (
    scope: string,
    resource: InventoryResource | undefined
  ) => boolean;

  refreshRoles: () => Promise<void>;
} | null>(null);

export const RolesProvider = ({ children }: PropsWithChildren) => {
  const { nonProjectDal: dal } = useNonProjectDal();

  const { user } = useUser();

  const { data: rolesBody, mutate: reloadRoles } = dal.roles.useList();
  const { data: permissionsBody } = dal.roles.useListPermissions();

  const roles = (rolesBody?.roles ?? []).sort(rolesComparer);
  const rolesMap = Object.assign(
    {},
    ...roles.map((role: Role) => ({ [role.id]: role }))
  );

  const permissions = (permissionsBody?.permissions ?? []).reduce<
    Record<string, PermissionDescriptor>
  >((acc: Record<string, PermissionDescriptor>, permission: Permission) => {
    if (Permissions[permission.permissionType]) {
      acc[permission.permissionType] = {
        ...Permissions[permission.permissionType],
        ...permission,
      };
    }

    return acc;
  }, {});

  return (
    <RolesContext.Provider
      value={{
        roles,
        rolesMap,
        permissions,
        isGlobalRole: (role) =>
          !!role?.permissionGrants.some(
            (grant) =>
              permissions[grant.permission]?.category ===
              PermissionCategory.PERMISSION_CATEGORY_GLOBAL_MANAGEMENT
          ),
        isAuthorized: (scope, roleOverride) =>
          isAuthorized(
            scope,
            roleOverride?.scopes || user?.selectedRole.scopes || []
          ),
        isAuthorizedResource: (scope, resource) => {
          if (!resource) {
            return false;
          }

          if (!resource.metadata?.disallowedScopes) {
            return isAuthorized(scope, user?.selectedRole.scopes || []);
          }

          if (resource.metadata.disallowedScopes.includes(scope)) {
            return false;
          }

          return isAuthorized(scope, user?.selectedRole.scopes || []);
        },
        refreshRoles: async () => {
          await reloadRoles();
        },
      }}
    >
      {children}
    </RolesContext.Provider>
  );
};
